import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { adminGuard } from './core/guards/admin-guard';
import { permissionGuard } from './core/guards/permission.guard';
import { TODAS_LAS_CLAVES_BODEGA } from './core/constants/bodega-permisos';
import { CLAVES_ENTRADA_CATALOGO, TODAS_LAS_CLAVES_CATALOGO } from './modules/catalogo/catalogo-permisos';
import { catalogoFueraDeBodegaGuard } from './modules/catalogo/catalogo-redirecciones';
import { GRUPOS_CENTRAL, TODAS_LAS_CLAVES_CENTRAL } from './modules/bodega-central/bodega-central-permisos';
import { CLAVES_EPP_OBRA } from './modules/bodega-central/util/epp';
import { menuGuard } from './modules/menu/menu.guard';
import { obraGuard } from './core/navegacion/obra-global.service';
import { elegirObraGuard, obrasASisconGuard } from './modules/menu/obras-a-siscon.guard';
import { MainLayout } from './layout/components/main-layout/main-layout';

export const routes: Routes = [
  // ── Auth ───────────────────────────────────────────────────────────────
  {
    path: 'auth',
    loadChildren: () => import('./modules/auth/auth-module').then(m => m.AuthModule)
  },

  // ── Soporte público (sin autenticación) ────────────────────────────────
  {
    path: 'soporte',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/soporte/pages/nuevo-ticket/nuevo-ticket').then(m => m.NuevoTicket)
  },

  // ── Tarjeta digital pública (sin autenticación) ────────────────────────
  {
    path: 'tarjeta/:slug',
    loadComponent: () =>
      import('./modules/tarjetas/pages/vista-tarjeta/vista-tarjeta').then(m => m.VistaTarjeta)
  },

  // ── Evaluación pública por token (sin autenticación) ───────────────────
  {
    path: 'evaluacion/ver/:token',
    loadComponent: () =>
      import('./modules/evaluacion/pages/ver-evaluacion/ver-evaluacion').then(m => m.VerEvaluacion)
  },

  // ── Recuperación de contraseña (sin autenticación) ─────────────────────
  {
    path: 'recuperar-password',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/auth/pages/recuperar-password/recuperar-password').then(m => m.RecuperarPassword)
  },
  {
    path: 'recuperar-password/:token',
    loadComponent: () =>
      import('./modules/auth/pages/reset-password/reset-password').then(m => m.ResetPassword)
  },
  {
    path: 'activar-cuenta/:token',
    loadComponent: () =>
      import('./modules/auth/pages/activar-cuenta/activar-cuenta').then(m => m.ActivarCuenta)
  },

  // ── Cambiar contraseña (con auth) ──────────────────────────────────────
  {
    path: 'cambiar-contrasena',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./modules/auth/pages/change-password/change-password').then(m => m.ChangePassword)
  },

  // ── "Ver completo" de un lead relacionado (con auth, sin permiso de módulo) ────────────
  // Se abre en una pestaña nueva desde el panel "Información relacionada" del drawer. La
  // autorización real (¿leadDestinoId está de verdad relacionado a leadOrigenId?) la hace el
  // backend — acá solo se exige estar logueado, igual que cambiar-contrasena arriba.
  {
    path: 'comercial/leads/relacionado/:leadOrigenId/:leadDestinoId',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./modules/comercial/pages/lead-relacionado/lead-relacionado').then(m => m.LeadRelacionado)
  },

  // ── Área protegida: MainLayout + authGuard ─────────────────────────────
  {
    path: '',
    component: MainLayout,
    canActivate: [authGuard],
    // Antes de abrir una pantalla de UNA obra deja la obra activa lista (enlace con otra obra, o la activa no sirve para el módulo).
    canActivateChild: [obraGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./modules/home/home-module').then(m => m.HomeModule)
      },
      // Pantallas de menú del hub nuevo: Inicio › Departamento › Botón › tarjetas. Las dirige el registro de módulos (core/navegacion/registro.ts).
      {
        path: 'menu/:dep',
        pathMatch: 'full',
        canActivate: [menuGuard],
        loadComponent: () => import('./modules/menu/menu-pantalla').then(m => m.MenuPantalla),
      },
      {
        path: 'menu/:dep/:boton',
        canActivate: [menuGuard],
        loadComponent: () => import('./modules/menu/menu-pantalla').then(m => m.MenuPantalla),
      },
      {
        path: 'empresas',
        data: { claveModulo: 'empresas' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/empresas/empresas-module').then(m => m.EmpresasModule)
      },
      // Empresas y obras (noche 10): el árbol de empresas y sus obras (antes «Administración de obras» y «Administración de empresas»). La puerta es la clave `empresas_obras`; las acciones de adentro piden las de siempre.
      {
        path: 'empresas-obras',
        data: { claveModulo: 'empresas_obras' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/empresas-obras/empresas-obras.routes').then(m => m.EMPRESAS_OBRAS_ROUTES)
      },
      // Zonificación y unidades (noche 10): las zonas, las unidades y el mapa de UNA obra. La obra va en la dirección (/zonificacion/:obraId) y la clave `zonificacion_unidades` se mide EN esa obra.
      {
        path: 'zonificacion',
        data: { claveModulo: 'zonificacion_unidades' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/zonificacion/zonificacion.routes').then(m => m.ZONIFICACION_ROUTES)
      },
      // RRHH dentro de una obra (Fase 8): las pantallas de /trabajadores acotadas a la obra de la dirección. Va ANTES de 'obras':
      // no cuelga de /obras/** (que exige el permiso 'obras'), cada hija exige el suyo con alcance sobre esa obra (permisoEnObraGuard).
      {
        path: 'obras/:obraId/rrhh',
        loadChildren: () => import('./modules/trabajadores/trabajadores-obra.routes').then(m => m.TRABAJADORES_OBRA_ROUTES)
      },
      // /obras a secas era el hub de módulos de la obra: ahora esas tarjetas viven en Siscon (/menu/siscon). El enlace de antes redirige (obrasASisconGuard).
      // Va ANTES del módulo de Obras y solo toma /obras a secas; el resto de /obras/** sigue donde estaba, con su guard.
      {
        path: 'obras',
        pathMatch: 'full',
        canActivate: [obrasASisconGuard],
        children: []
      },
      // La pantalla completa donde se elige la obra de un módulo de una sola obra (la de Avances). Se mantiene por compatibilidad, pero las tarjetas
      // ya no pasan por ella: abren el módulo directo con la obra que sirve (NavegacionService.abrir). Va ANTES del módulo de Obras: la abre quien ve
      // alguna tarjeta, no solo quien tiene el permiso «obras».
      {
        path: 'obras/elegir-obra/:modulo',
        canActivate: [elegirObraGuard],
        loadComponent: () => import('./modules/obras/pages/seleccion-obra/seleccion-obra-modulo').then(m => m.SeleccionObraModulo)
      },
      {
        path: 'obras',
        data: { claveModulo: 'obras' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/obras/obras-module').then(m => m.ObrasModule)
      },
      {
        path: 'trabajadores',
        data: { claveModulo: 'trabajadores' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/trabajadores/trabajadores-module').then(m => m.TrabajadoresModule)
      },
      {
        path: 'cuadrillas',
        data: { claveModulo: 'cuadrillas' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/cuadrillas/cuadrillas-module').then(m => m.CuadrillasModule)
      },
      {
        path: 'produccion',
        data: { claveModulo: 'produccion' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/produccion/produccion-module').then(m => m.ProduccionModule)
      },
      // Bodega tiene ruta y permisos propios (no cuelga de /obras): un bodeguero con alguna de sus
      // cuatro claves (una por pestaña) entra sin necesitar 'obras', que además abre la administración de
      // obras. La obra va en la dirección (/bodega/:obraId/...) y cada pestaña exige la suya (ver bodega-routing-module.ts).
      // `/bodega?ir=catalogo|prefijos` (enlaces de antes) va al módulo Catálogo antes de mirar el permiso de Bodega.
      {
        path: 'bodega',
        data: { claveModulo: TODAS_LAS_CLAVES_BODEGA },
        canActivate: [catalogoFueraDeBodegaGuard, permissionGuard],
        loadChildren: () => import('./modules/bodega/bodega-module').then(m => m.BodegaModule)
      },
      // Catálogo: módulo propio, de toda la empresa (sin obra ni selector de obra). Se entra con cualquiera de sus tres claves y cada pestaña
      // exige la suya (ver modules/catalogo/catalogo.routes.ts). Antes eran las pestañas Catálogo y Prefijos de Bodega.
      {
        path: 'catalogo',
        data: { claveModulo: CLAVES_ENTRADA_CATALOGO },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/catalogo/catalogo.routes').then(m => m.CATALOGO_ROUTES)
      },
      // Bodega Central: de una empresa, sin obra (nada bajo /bodega/**, que exige una obra). Se entra con cualquiera de sus nueve claves o de las
      // del Catálogo (su pestaña Catálogo enlaza a ese módulo); cada pestaña exige la suya (ver modules/bodega-central/bodega-central.routes.ts).
      {
        path: 'bodega-central',
        data: { claveModulo: [...TODAS_LAS_CLAVES_CENTRAL, ...TODAS_LAS_CLAVES_CATALOGO] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.BODEGA_CENTRAL_ROUTES)
      },
      // La Central se reparte en cinco tarjetas (Siscon › Administración de empresa); cada una con su raíz y sus claves (los permisos no cambiaron: solo dónde se ofrece cada pantalla).
      {
        path: 'precios-cobros',
        data: { claveModulo: [...GRUPOS_CENTRAL.cobros.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.PRECIOS_COBROS_ROUTES)
      },
      {
        path: 'arriendos-maquinaria',
        data: { claveModulo: [...GRUPOS_CENTRAL.arriendos.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.ARRIENDOS_MAQUINARIA_ROUTES)
      },
      // Andamios y moldajes (Etapa 8): recetas de armado, piezas, solicitudes de las obras, contratos y tarifas, con el mismo marco que las otras tarjetas de la Central.
      {
        path: 'andamios-moldajes',
        data: { claveModulo: [...GRUPOS_CENTRAL.andamios.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.ANDAMIOS_MOLDAJES_ROUTES)
      },
      // Activos fijos con etiqueta QR HEXXA e implementos (etapa 9): lista, escaneo, levantamiento por lugar y etiquetas, con el marco de las otras tarjetas de la Central.
      {
        path: 'activos-implementos',
        data: { claveModulo: [...GRUPOS_CENTRAL.activos.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.ACTIVOS_IMPLEMENTOS_ROUTES)
      },
      // EPP con vale digital (etapa 10), lado de la Central: vales por visar, entregas urgentes, ficha de EPP de cada trabajador y configuración. Mismo marco que las otras tarjetas de la Central.
      {
        path: 'epp',
        data: { claveModulo: [...GRUPOS_CENTRAL.epp.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.EPP_ROUTES)
      },
      // Panel de control de la Central (etapa 12, parte B): una sola pantalla de lectura con los indicadores de toda la Central. Mismo marco que las otras tarjetas.
      {
        path: 'panel-central',
        data: { claveModulo: [...GRUPOS_CENTRAL.panel.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.PANEL_CENTRAL_ROUTES)
      },
      {
        path: 'configuracion-central',
        data: { claveModulo: [...GRUPOS_CENTRAL.configuracion.claves] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/bodega-central/bodega-central.routes').then(m => m.CONFIGURACION_CENTRAL_ROUTES)
      },
      // Documentos (OC y facturas): la obra va en la dirección (/documentos/:obraId) y las claves se miden EN esa obra (el servidor las exige en cada llamada). Mis documentos, Nuevo documento, la ficha y Gestionar documentos. Perezoso: no pesa en el login ni en la carga inicial.
      {
        path: 'documentos',
        data: { claveModulo: ['documentos', 'documentos_gestionar'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/documentos/documentos.routes').then(m => m.DOCUMENTOS_ROUTES)
      },
      // Cobros que RECIBE una obra (pata 3): la obra va en la dirección (/cobros-recibidos/:obraId) y la clave `obra_cobros` se mide EN esa obra (el servidor la exige en cada
      // llamada). Se aprueba o se rechaza cada cobro; ver modules/cobros-recibidos.
      {
        path: 'cobros-recibidos',
        data: { claveModulo: 'obra_cobros' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/cobros-recibidos/cobros-recibidos.routes').then(m => m.COBROS_RECIBIDOS_ROUTES)
      },
      // Panel de la obra (etapa 12, parte B): los indicadores de UNA obra. La obra va en la dirección (/panel-obra/:obraId) y la clave `obra_cobros` se mide EN esa obra; cada indicador pide además la clave de lo que muestra.
      {
        path: 'panel-obra',
        data: { claveModulo: 'obra_cobros' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/panel-obra/panel-obra.routes').then(m => m.PANEL_OBRA_ROUTES)
      },
      // Solicitud de maquinaria de una obra (pata 4): la obra va en la dirección (/arriendos/:obraId) y la clave `obra_arriendos` se mide EN esa obra (el servidor la exige en cada
      // llamada). La obra pide, ve el estado, acepta o rechaza una alternativa, cancela, pide extensiones y devuelve; ver modules/arriendos.
      {
        path: 'arriendos',
        data: { claveModulo: 'obra_arriendos' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/arriendos/arriendos.routes').then(m => m.ARRIENDOS_ROUTES)
      },
      // Andamios y moldajes de una obra (Etapa 8): la obra va en la dirección (/andamios/:obraId/<pestaña>) y la clave `obra_andamios` se mide EN esa obra (el servidor la exige en cada
      // llamada). La obra pide, ve lo que tiene entregado y qué puede armar con eso, devuelve y cancela; ver modules/andamios-obra.
      {
        path: 'andamios',
        data: { claveModulo: 'obra_andamios' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/andamios-obra/andamios-obra.routes').then(m => m.ANDAMIOS_OBRA_ROUTES)
      },
      // Los activos asignados a una obra (etapa 9): la obra va en la dirección (/activos-obra/:obraId) y la clave `obra_activos` se mide EN esa obra (el servidor la exige en cada llamada).
      // La obra ve lo que tiene asignado y confirma la recepción escaneando la etiqueta; ver modules/activos-obra.
      {
        path: 'activos-obra',
        data: { claveModulo: 'obra_activos' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/activos-obra/activos-obra.routes').then(m => m.ACTIVOS_OBRA_ROUTES)
      },
      // EPP con vale digital (etapa 10), lado de la obra: la obra va en la dirección (/epp-obra/:obraId) y las cuatro claves `obra_epp*` se miden EN esa obra (el servidor las exige en cada llamada).
      // Se piden vales, se entregan y se devuelven, se ve la ficha de cada trabajador y cada trabajador ve su propio EPP; ver modules/epp-obra. También vive como pestaña de Bodega (/bodega/:obraId/epp).
      {
        path: 'epp-obra',
        data: { claveModulo: [...CLAVES_EPP_OBRA] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/epp-obra/epp-obra.routes').then(m => m.EPP_OBRA_ROUTES)
      },
      // Operación de la maquinaria arrendada que está en una obra (pata 5, etapas B y C): la obra va en la dirección y cada clave (`obra_operadores`, `obra_bitacora`,
      // `obra_bitacora_propia`) se mide EN esa obra (el servidor la exige en cada llamada). Operadores: quién opera cada máquina. Bitácora: el día del operador y su validación.
      // «Maquinaria de la obra» (Administración de obra): una sola entrada para Solicitudes, Operadores y Bitácoras. La raíz lleva a la primera pestaña que el perfil abre EN esa obra; cada
      // pestaña sigue en su dirección de siempre (/arriendos, /operadores-maquinaria, /bitacora-maquinaria).
      {
        path: 'maquinaria',
        data: { claveModulo: ['obra_arriendos', 'obra_operadores'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/operacion/maquinaria.routes').then(m => m.MAQUINARIA_ROUTES)
      },
      {
        path: 'operadores-maquinaria',
        data: { claveModulo: 'obra_operadores' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/operacion/operadores.routes').then(m => m.OPERADORES_ROUTES)
      },
      {
        path: 'bitacora-maquinaria',
        data: { claveModulo: ['obra_bitacora', 'obra_bitacora_propia'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/operacion/bitacora.routes').then(m => m.BITACORA_ROUTES)
      },
      {
        path: 'combustible-maquinaria',
        data: { claveModulo: ['obra_bitacora', 'obra_bitacora_propia'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/operacion/combustible.routes').then(m => m.COMBUSTIBLE_ROUTES)
      },
      {
        path: 'uso-vehiculo',
        data: { claveModulo: ['obra_bitacora', 'obra_bitacora_propia'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/operacion/uso-vehiculo.routes').then(m => m.USO_VEHICULO_ROUTES)
      },
      {
        path: 'solicitudes',
        data: { claveModulo: 'solicitudes' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/solicitudes/solicitudes-module').then(m => m.SolicitudesModule)
      },
      {
        path: 'reportes',
        data: { claveModulo: 'reportes' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/reportes/reportes-module').then(m => m.ReportesModule)
      },
      // Perfil de cuenta: disponible para cualquier usuario autenticado, sin gate de permisos por módulo
      {
        path: 'configuracion/perfil',
        loadComponent: () =>
          import('./modules/configuracion/pages/perfil-page/perfil-page').then(m => m.PerfilPage)
      },
      // Procedimientos: disponible para cualquier usuario autenticado, sin gate de permisos por módulo
      {
        path: 'procedimientos',
        loadChildren: () => import('./modules/procedimientos/procedimientos-module').then(m => m.ProcedimientosModule)
      },
      {
        path: 'configuracion',
        data: { claveModulo: 'configuracion' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/configuracion/configuracion-module').then(m => m.ConfiguracionModule)
      },
      {
        path: 'combustible',
        data: { claveModulo: 'combustible' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/combustible/combustible-module').then(m => m.CombustibleModule)
      },
      {
        path: 'evaluacion',
        data: { claveModulo: 'evaluacion' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/evaluacion/evaluacion-module').then(m => m.EvaluacionModule)
      },
      {
        // "Cualquiera de las dos" a nivel del padre — deja entrar con solo comercial_leads,
        // solo comercial_vendedoras, o ambos. Cada ruta hija (leads, vendedoras-proyectos)
        // exige su propia clave puntual, ver comercial-routing-module.ts.
        path: 'comercial',
        data: { claveModulo: ['comercial_leads', 'comercial_vendedoras'] },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/comercial/comercial-module').then(m => m.ComercialModule)
      },
      {
        path: 'ventas',
        data: { claveModulo: 'comercial_ventas' },
        canActivate: [permissionGuard],
        loadChildren: () => import('./modules/ventas/ventas-module').then(m => m.VentasModule)
      },
      {
        path: 'admin',
        canActivate: [adminGuard],
        loadChildren: () => import('./modules/admin/admin-module').then(m => m.AdminModule)
      },
      {
        path: 'aprobaciones',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./modules/aprobaciones/pages/pendientes/pendientes').then(m => m.AprobacionesPendientes)
      },
      // Notificaciones: la lista completa de la campana (Etapa 11, parte B). Sin permiso de módulo: cualquier sesión tiene la suya.
      {
        path: 'notificaciones',
        loadComponent: () =>
          import('./modules/notificaciones/pages/notificaciones/notificaciones').then(m => m.Notificaciones)
      },
      // Soporte (rutas autenticadas)
      {
        path: 'soporte',
        children: [
          {
            path: 'mis-tickets',
            loadComponent: () =>
              import('./modules/soporte/pages/mis-tickets/mis-tickets').then(m => m.MisTickets)
          },
          {
            path: 'administracion',
            canActivate: [permissionGuard],
            data: { claveModulo: 'soporte_admin' },
            loadComponent: () =>
              import('./modules/soporte/pages/administracion/administracion').then(m => m.AdministracionSoporte)
          }
        ]
      },
      // Causas laborales
      {
        path: 'causas-laborales',
        data: { claveModulo: 'causas-laborales' },
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./modules/causas-laborales/causas-laborales-module').then(m => m.CausasLaboralesModule)
      },
      // Presupuestos legales
      {
        path: 'presupuestos-legales',
        data: { claveModulo: 'presupuestos-legales' },
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./modules/presupuestos-legales/presupuestos-legales-module').then(m => m.PresupuestosLegalesModule)
      },
      // Tarjetas digitales de vendedores
      {
        path: 'tarjetas',
        data: { claveModulo: 'tarjetas' },
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('./modules/tarjetas/tarjetas-module').then(m => m.TarjetasModule)
      },
      // Acceso denegado
      {
        path: 'sin-acceso',
        loadComponent: () => import('./pages/sin-acceso/sin-acceso').then(m => m.SinAcceso)
      }
    ]
  },

  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found').then(m => m.NotFound)
  }
];
